import React, { useState, useEffect, useCallback, useRef } from 'react';

const MIN_COL_W = 40;
const MIN_ROW_H = 24;

export default function GridElement({
  shape, isSelected, tool, fadedShapes,
  onSelect, onShapeUpdate, getPointerCoords, svgRef, panRef,
}) {
  const [editingCell, setEditingCell] = useState(null);
  const [hovered, setHovered] = useState(false);
  const cellRefs = useRef({});

  const rows = shape.rows || 3;
  const cols = shape.cols || 3;
  const cells = shape.cells || Array.from({ length: rows }, () => Array(cols).fill(''));

  // Per-column widths and per-row heights (with fallback from total width/height)
  const colWidths = shape.colWidths || Array(cols).fill(Math.round((shape.width || cols * 120) / cols));
  const rowHeights = shape.rowHeights || Array(rows).fill(Math.round((shape.height || rows * 44) / rows));

  const totalW = colWidths.reduce((s, w) => s + w, 0);
  const totalH = rowHeights.reduce((s, h) => s + h, 0);
  const avgColW = Math.round(totalW / cols);
  const avgRowH = Math.round(totalH / rows);
  const faded = fadedShapes?.has(shape.id);

  // Cumulative positions of column right-edges and row bottom-edges (for handle placement)
  const colEdges = colWidths.reduce((acc, w) => [...acc, (acc[acc.length - 1] || 0) + w], []);
  const rowEdges = rowHeights.reduce((acc, h) => [...acc, (acc[acc.length - 1] || 0) + h], []);

  // Focus the cell and set its content when editing starts
  useEffect(() => {
    if (editingCell === null) return;
    const { row, col } = editingCell;
    const el = cellRefs.current[`${row}-${col}`];
    if (!el) return;
    el.innerText = cells[row]?.[col] || '';
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(range); }
  }, [editingCell]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCell = useCallback((row, col, el) => {
    const newText = el.innerText || '';
    if ((cells[row]?.[col] ?? '') === newText) return;
    const newCells = cells.map((r, ri) =>
      r.map((c, ci) => ri === row && ci === col ? newText : c)
    );
    onShapeUpdate(shape.id, { cells: newCells });
  }, [cells, shape.id, onShapeUpdate]);

  // Column resize — redistributes between col i and col i+1, total width stays fixed
  const handleColResize = useCallback((e, colIdx) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colIdx];
    const nextW = colWidths[colIdx + 1];
    const scale = panRef.current?.scale || 1;

    const onMove = (ev) => {
      const delta = (ev.clientX - startX) / scale;
      const newW = Math.max(MIN_COL_W, Math.round(startW + delta));
      const actualDelta = newW - startW;
      const newNextW = Math.max(MIN_COL_W, Math.round(nextW - actualDelta));
      const newColWidths = colWidths.map((w, i) =>
        i === colIdx ? newW : i === colIdx + 1 ? newNextW : w
      );
      onShapeUpdate(shape.id, {
        colWidths: newColWidths,
        width: newColWidths.reduce((s, w) => s + w, 0),
      });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [colWidths, shape.id, onShapeUpdate, panRef]);

  // Row resize — redistributes between row i and row i+1, total height stays fixed
  const handleRowResize = useCallback((e, rowIdx) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startH = rowHeights[rowIdx];
    const nextH = rowHeights[rowIdx + 1];
    const scale = panRef.current?.scale || 1;

    const onMove = (ev) => {
      const delta = (ev.clientY - startY) / scale;
      const newH = Math.max(MIN_ROW_H, Math.round(startH + delta));
      const actualDelta = newH - startH;
      const newNextH = Math.max(MIN_ROW_H, Math.round(nextH - actualDelta));
      const newRowHeights = rowHeights.map((h, i) =>
        i === rowIdx ? newH : i === rowIdx + 1 ? newNextH : h
      );
      onShapeUpdate(shape.id, {
        rowHeights: newRowHeights,
        height: newRowHeights.reduce((s, h) => s + h, 0),
      });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [rowHeights, shape.id, onShapeUpdate, panRef]);

  const addColumn = useCallback((e) => {
    e.stopPropagation();
    onShapeUpdate(shape.id, {
      cells: cells.map(row => [...row, '']),
      cols: cols + 1,
      colWidths: [...colWidths, avgColW],
      width: totalW + avgColW,
    });
  }, [cells, cols, colWidths, avgColW, totalW, shape.id, onShapeUpdate]);

  const addRow = useCallback((e) => {
    e.stopPropagation();
    onShapeUpdate(shape.id, {
      cells: [...cells, Array(cols).fill('')],
      rows: rows + 1,
      rowHeights: [...rowHeights, avgRowH],
      height: totalH + avgRowH,
    });
  }, [cells, cols, rows, rowHeights, avgRowH, totalH, shape.id, onShapeUpdate]);

  const handlePointerDown = useCallback((e) => {
    if (tool === 'pen' || tool === 'highlighter' || tool === 'eraser') return;
    if (editingCell !== null) return;
    e.stopPropagation();
    onSelect(shape.id);

    const { x: bx, y: by } = getPointerCoords(e, panRef.current, svgRef);
    const ox = shape.x, oy = shape.y;

    const onMove = (ev) => {
      const { x, y } = getPointerCoords(ev, panRef.current, svgRef);
      onShapeUpdate(shape.id, { x: ox + x - bx, y: oy + y - by });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [shape.id, shape.x, shape.y, tool, editingCell, onSelect, onShapeUpdate, getPointerCoords, panRef, svgRef]);

  const showHandles = hovered || isSelected;

  const plusBtn = {
    width: 22, height: 22,
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 400,
    boxShadow: '0 1px 4px rgba(59,130,246,0.4)',
    padding: 0,
    flexShrink: 0,
  };

  return (
    <foreignObject
      x={shape.x} y={shape.y}
      width={totalW + 48} height={totalH + 48}
      style={{ overflow: 'visible', opacity: faded ? 0.3 : 1 }}
    >
      <div
        onPointerDown={handlePointerDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ position: 'relative', width: totalW, height: totalH }}
      >
        {/* Selection ring */}
        {isSelected && (
          <div style={{
            position: 'absolute', inset: -2,
            border: '2px solid #3b82f6',
            borderRadius: 6,
            pointerEvents: 'none',
            zIndex: 20,
          }} />
        )}

        <table style={{
          borderCollapse: 'collapse',
          width: totalW, height: totalH,
          tableLayout: 'fixed',
          background: 'transparent',
          userSelect: editingCell ? 'text' : 'none',
          cursor: editingCell ? 'default' : 'grab',
        }}>
          <colgroup>
            {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
          <tbody>
            {cells.map((row, ri) => (
              <tr key={ri} style={{ height: rowHeights[ri] || avgRowH }}>
                {row.map((cell, ci) => {
                  const isHeader = ri === 0 && shape.headerRow !== false;
                  const isEditing = editingCell?.row === ri && editingCell?.col === ci;
                  return (
                    <td
                      key={ci}
                      ref={el => { if (el) cellRefs.current[`${ri}-${ci}`] = el; }}
                      contentEditable={isEditing || undefined}
                      suppressContentEditableWarning
                      onDoubleClick={e => {
                        if (tool === 'pen' || tool === 'highlighter' || tool === 'eraser') return;
                        e.stopPropagation();
                        onSelect(shape.id);
                        setEditingCell({ row: ri, col: ci });
                      }}
                      onBlur={isEditing ? e => {
                        saveCell(ri, ci, e.target);
                        setEditingCell(null);
                      } : undefined}
                      onKeyDown={isEditing ? e => {
                        e.stopPropagation();
                        if (e.key === 'Escape') { e.target.blur(); return; }
                        if (e.key === 'Tab') {
                          e.preventDefault();
                          saveCell(ri, ci, e.target);
                          const nc = ci + 1 < cols ? ci + 1 : 0;
                          const nr = ci + 1 < cols ? ri : (ri + 1 < rows ? ri + 1 : 0);
                          setEditingCell({ row: nr, col: nc });
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          saveCell(ri, ci, e.target);
                          setEditingCell({ row: ri + 1 < rows ? ri + 1 : ri, col: ci });
                        }
                      } : undefined}
                      style={{
                        border: '1px solid rgba(0,0,0,0.15)',
                        padding: '6px 10px',
                        background: isEditing
                          ? 'rgba(255,255,255,0.95)'
                          : isHeader ? 'rgba(0,0,0,0.06)' : 'transparent',
                        fontWeight: isHeader ? '600' : 'normal',
                        fontSize: 14,
                        fontFamily: 'Arial, sans-serif',
                        color: '#111',
                        verticalAlign: 'middle',
                        overflow: 'hidden',
                        outline: isEditing ? '2px solid #3b82f6' : 'none',
                        outlineOffset: -1,
                        cursor: isEditing ? 'text' : 'inherit',
                        userSelect: isEditing ? 'text' : 'none',
                        boxSizing: 'border-box',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {!isEditing && cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Column resize handles (between each pair of columns) */}
        {showHandles && colEdges.slice(0, -1).map((x, i) => (
          <div
            key={`ch-${i}`}
            onPointerDown={e => handleColResize(e, i)}
            style={{
              position: 'absolute',
              left: x - 3,
              top: 0,
              width: 6,
              height: totalH,
              cursor: 'col-resize',
              zIndex: 15,
              background: 'transparent',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          />
        ))}

        {/* Row resize handles (between each pair of rows) */}
        {showHandles && rowEdges.slice(0, -1).map((y, i) => (
          <div
            key={`rh-${i}`}
            onPointerDown={e => handleRowResize(e, i)}
            style={{
              position: 'absolute',
              left: 0,
              top: y - 3,
              width: totalW,
              height: 6,
              cursor: 'row-resize',
              zIndex: 15,
              background: 'transparent',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          />
        ))}

        {/* Add column button — right edge */}
        {showHandles && (
          <button
            onPointerDown={addColumn}
            style={{ ...plusBtn, position: 'absolute', right: -30, top: '50%', transform: 'translateY(-50%)' }}
            title="Add column"
          >+</button>
        )}

        {/* Add row button — bottom edge */}
        {showHandles && (
          <button
            onPointerDown={addRow}
            style={{ ...plusBtn, position: 'absolute', bottom: -30, left: '50%', transform: 'translateX(-50%)' }}
            title="Add row"
          >+</button>
        )}
      </div>
    </foreignObject>
  );
}
